// Cubus Page SDK — инжектится хостом CbsHtmlSandbox в кастомные HTML-страницы.
// Протокол моста описан в docs/custom-html-sdk.md (корень репозитория).
//
// ВАЖНО: cubusPageSdkMain сериализуется через Function.prototype.toString и
// исполняется внутри sandbox-iframe, где нет babel-хелперов и модулей.
// Поэтому внутри функции запрещены конструкции, которые babel транспилирует
// с внешними хелперами: async/await, spread, деструктуризация, классы, for-of.
// Только простой ES5 + стрелки + шаблонные строки + Promise.

/* eslint-disable prefer-rest-params */

export function cubusPageSdkMain(frameId) {
  // Версия протокола моста. Обязана совпадать с PROTOCOL_VERSION хоста (CbsHtmlSandbox):
  // SDK инжектится хостом, это одна и та же сборка. Разъехавшись, они дали бы странице
  // два разных ответа на один вопрос — `env().protocol` = 1 против `protocolversion` = 2,
  // что и вылезло на прод-приёмке PROP-048.
  const VERSION = 2
  const parentWin = window.parent
  const pending = {}
  let seq = 0
  let context = null
  let initWaiters = []
  const listeners = {
    init: [], context: [], theme: [], refresh: [], visibility: [], destroy: [],
  }

  function sdkError(code, message, details) {
    const e = new Error(message)
    e.code = code
    e.details = details === undefined ? null : details
    return e
  }

  function send(msg) {
    const m = msg
    m.cubus = true
    m.v = VERSION
    m.frame = frameId
    parentWin.postMessage(m, '*')
  }

  function request(type, payload, timeoutMs) {
    return new Promise(((resolve, reject) => {
      seq += 1
      const id = `q-${seq}`
      const timer = setTimeout(() => {
        delete pending[id]
        reject(sdkError('E_TIMEOUT', `request "${type}" timed out`))
      }, timeoutMs || 60000)
      pending[id] = { resolve, reject, timer }
      send({
        kind: 'req', id, type, payload: payload || {},
      })
    }))
  }

  function whenInit() {
    if (context) return Promise.resolve()
    return new Promise((resolve => { initWaiters.push(resolve) }))
  }

  function dataRequest(type, payload, timeoutMs) {
    return whenInit().then(() => request(type, payload, timeoutMs))
  }

  function fire(name, arg) {
    listeners[name].forEach(fn => {
      try { fn(arg) } catch (e) { console.error('[cubus-sdk] listener error', e) }
    })
  }

  function applyTheme(theme) {
    if (!theme) return
    const root = document.documentElement
    root.setAttribute('data-theme', theme.skin === 'dark' ? 'dark' : 'light')
    const colors = theme.colors || {}
    Object.keys(colors).forEach(key => {
      root.style.setProperty(`--cubus-${key}`, colors[key])
    })
  }

  window.addEventListener('message', event => {
    const msg = event.data
    if (!msg || msg.cubus !== true || msg.frame !== frameId) return
    if (msg.kind === 'res') {
      const p = pending[msg.id]
      if (!p) return
      delete pending[msg.id]
      clearTimeout(p.timer)
      if (msg.error) {
        p.reject(sdkError(msg.error.code || 'E_INTERNAL', msg.error.message || 'error', msg.error.details))
      } else {
        p.resolve(msg.result)
      }
      return
    }
    if (msg.kind !== 'event') return
    if (msg.type === 'init' || msg.type === 'contextChanged') {
      const first = !context
      context = msg.payload
      applyTheme(context && context.theme)
      if (first) {
        fire('init', context)
        initWaiters.forEach(fn => { fn() })
        initWaiters = []
      }
      fire('context', context)
    } else if (msg.type === 'themeChanged') {
      applyTheme(msg.payload && msg.payload.theme)
      fire('theme', msg.payload && msg.payload.theme)
    } else if (msg.type === 'refresh') {
      fire('refresh', null)
    } else if (msg.type === 'visibility') {
      fire('visibility', msg.payload)
    } else if (msg.type === 'destroy') {
      fire('destroy', null)
    }
  })

  // BUG-427: внутренние ссылки страницы уводили САМ ФРЕЙМ.
  //
  // Механика такая: документ, отданный через `srcdoc`, берёт базовый адрес у РОДИТЕЛЯ, поэтому
  // `/cubus/objects` разрешается в адрес Cubus, и фрейм послушно открывает его В СЕБЕ. Человек
  // видел приложение, загруженное в рамку размером с область презентации, вместо перехода на
  // объект. Запретить фрейму уходить по своему адресу нельзя — ни `sandbox`, ни CSP такой ручки
  // не дают, — поэтому клик перехватываем и просим ХОСТ перейти маршрутизатором.
  //
  // Перехватываем на фазе ЗАХВАТА и только то, что заведомо внутреннее: якорь `#`, чужой домен,
  // клик с модификатором (человек хочет новую вкладку) и уже отменённое событие не трогаем.
  function internalPath(a) {
    const raw = a.getAttribute('href') || ''
    if (!raw || raw.charAt(0) === '#') return ''
    // Путь от корня — то, что просит писать промпт агента и чем пользуются свои страницы.
    if (raw.indexOf('/') === 0 && raw.indexOf('//') !== 0) return raw
    // Полный адрес СВОЕЙ установки: модель нет-нет да и напишет его целиком. Origin приходит
    // от хоста в контексте — сам фрейм своего происхождения не знает, оно у него обнулено.
    const origin = context && context.origin
    if (origin && a.href && a.href.indexOf(`${origin}/`) === 0) return a.href.slice(origin.length)
    return ''
  }
  // PROP-428: ВНЕШНЯЯ ссылка тоже уводила сам фрейм — чужой сайт рисовался внутри рамки Cubus, в
  // нашем окружении. Решение владельца: открывать её в новой вкладке. Сам фрейм вкладку открыть не
  // может и не должен (`allow-popups` песочнице не выдаётся, docs §9), поэтому просит ХОСТ; клик
  // человека во фрейме даёт хосту право открыть окно. Внешней считается только http(s) — `mailto:`
  // и прочие схемы остаются браузеру.
  function externalUrl(a) {
    const url = a.href || ''
    return /^https?:\/\//i.test(url) ? url : ''
  }
  document.addEventListener('click', event => {
    if (event.defaultPrevented || event.button !== 0) return
    let el = event.target
    while (el && String(el.nodeName).toUpperCase() !== 'A') el = el.parentElement
    if (!el) return
    const modified = event.metaKey || event.ctrlKey || event.shiftKey || event.altKey
    const path = modified ? '' : internalPath(el)
    if (path) {
      event.preventDefault()
      request('navigate', { path }, 10000).catch(e => {
        console.error('[cubus-sdk] переход по ссылке отклонён хостом', e)
      })
      return
    }
    const url = internalPath(el) ? '' : externalUrl(el)
    if (!url) return
    event.preventDefault()
    request('openExternal', { url }, 10000).catch(e => {
      console.error('[cubus-sdk] внешняя ссылка отклонена хостом', e)
    })
  }, true)

  // Авто-высота: следим за контентом и сообщаем хосту (дебаунс 100 мс)
  let lastHeight = 0
  let resizeTimer = null
  function reportHeight() {
    const { body } = document
    const h = Math.ceil(Math.max(
      body ? body.scrollHeight : 0,
      body ? body.offsetHeight : 0,
    ))
    if (!h || h === lastHeight) return
    lastHeight = h
    request('resize', { height: h }, 5000).catch(() => {})
  }
  function scheduleReport() {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(reportHeight, 100)
  }
  function startResizeWatch() {
    if (typeof ResizeObserver !== 'undefined') {
      const ro = new ResizeObserver(scheduleReport)
      ro.observe(document.body)
      ro.observe(document.documentElement)
    } else {
      setInterval(scheduleReport, 1000)
    }
    scheduleReport()
  }

  window.cubus = {
    getContext: function getContext() {
      return whenInit().then(() => context)
    },
    onInit: function onInit(fn) { listeners.init.push(fn) },
    onContextChange: function onContextChange(fn) { listeners.context.push(fn) },
    onTheme: function onTheme(fn) { listeners.theme.push(fn) },
    onRefresh: function onRefresh(fn) { listeners.refresh.push(fn) },
    onVisibility: function onVisibility(fn) { listeners.visibility.push(fn) },
    onDestroy: function onDestroy(fn) { listeners.destroy.push(fn) },
    query: function query(params) { return dataRequest('query', params) },
    save: function save(objectsid, record) {
      return dataRequest('save', { objectsid, record })
    },
    upsert: function upsert(objectsid, record, uniqueKeys) {
      return dataRequest('upsert', { objectsid, record, uniqueKeys })
    },
    remove: function remove(objectsid, record) {
      return dataRequest('delete', { objectsid, record })
    },
    runProc: function runProc(procsid, params) {
      return dataRequest('runProc', { procsid, params: params || {} }, 600000)
    },
    setValue: function setValue(attribute, value) {
      return dataRequest('setValue', { attribute, value })
    },
    navigate: function navigate(target) { return dataRequest('navigate', target || {}) },
    toast: function toast(kind, message) {
      request('toast', { kind, message }, 5000).catch(() => {})
    },
    emit: function emit(name, data) {
      send({ kind: 'event', type: 'action', payload: { name, data: data === undefined ? null : data } })
    },
    // PROP-048: страница узнавала об отсутствии операции только падением. Теперь набор
    // приезжает в контексте от хоста — он и диспетчеризует, поэтому только он знает правду;
    // VERSION здесь лишь версия инжектированного SDK.
    env: function env() {
      if (!context) return null
      return {
        mode: context.mode,
        locale: context.locale,
        protocol: VERSION,
        protocolversion: context.protocolversion || 1,
        operations: context.operations || [],
      }
    },
    // Отвечает только после init: до рукопожатия «нет такой операции» и «ещё не знаю»
    // неразличимы, а тихое false увело бы страницу на запасной путь без причины.
    supports: function supports(name) {
      if (!context) {
        throw sdkError('E_PROTOCOL',
          'cubus.supports() is available after init — call it inside cubus.onInit() or after cubus.getContext()')
      }
      return (context.operations || []).indexOf(name) !== -1
    },
  }

  function announce() {
    startResizeWatch()
    send({ kind: 'event', type: 'ready', payload: { v: VERSION } })
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', announce)
  } else {
    announce()
  }
}

// Собирает srcdoc: исходный HTML страницы + инжект SDK сразу после <head>.
// Замена через колбэк, чтобы спецпоследовательности ($&) в скрипте не ломали replace.
export function buildSandboxHtml(pageHtml, frameId) {
  // '</script>' собирается из частей, чтобы не оборвать инлайн-скрипт, если html попадёт в script-тег
  const script = ['<script>(', cubusPageSdkMain.toString(), ')(', JSON.stringify(frameId), ')</scr', 'ipt>'].join('')
  const html = pageHtml || ''
  const headRe = /<head[^>]*>/i
  if (headRe.test(html)) return html.replace(headRe, m => m + script)
  const htmlRe = /<html[^>]*>/i
  if (htmlRe.test(html)) return html.replace(htmlRe, m => `${m}<head>${script}</head>`)
  return script + html
}

export function newFrameId() {
  let out = 'f'
  for (let i = 0; i < 24; i += 1) {
    out += Math.floor(Math.random() * 16).toString(16)
  }
  return out
}
